import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import ConfirmActionModal from './ConfirmActionModal';
import useAppStore from '../../store';

const THROTTLE_STEPS = [25, 50, 75];

/**
 * T12.5 — design-critical: big, unmistakable, color-coded. Every action
 * routes through ConfirmActionModal (type CONFIRM) before mutating state.
 */
function EmergencyControls() {
  const consoleMode = useAppStore((state) => state.consoleMode);
  const throttleLimit = useAppStore((state) => state.throttleLimit);
  const engageKillSwitch = useAppStore((state) => state.engageKillSwitch);
  const engageThrottle = useAppStore((state) => state.engageThrottle);
  const engageSafeMode = useAppStore((state) => state.engageSafeMode);
  const disengageEmergency = useAppStore((state) => state.disengageEmergency);
  const [pendingAction, setPendingAction] = useState(null);

  const actions = {
    kill: {
      id: 'kill',
      shortLabel: 'kill switch',
      title: 'Engage kill switch',
      warning: 'Aborts every running task across all 10 agents and forces the fleet idle. In-flight work is lost.',
      run: engageKillSwitch
    },
    throttle: {
      id: 'throttle',
      shortLabel: 'throttle',
      title: `Throttle to ${THROTTLE_STEPS[0]}%`,
      warning: 'Caps agent concurrency. Queued tasks wait; nothing is lost. Adjust the percentage after engaging.',
      run: () => engageThrottle(THROTTLE_STEPS[0])
    },
    safe: {
      id: 'safe',
      shortLabel: 'safe mode',
      title: 'Engage safe mode',
      warning: 'Disables autonomy fleet-wide. Every agent action will require human approval until released.',
      run: engageSafeMode
    }
  };

  return (
    <div className={`emergency-controls ${consoleMode ? 'emergency-controls--engaged' : ''}`}>
      <div className="emergency-controls__banner">
        <FiIcons.FiAlertOctagon />
        <div>
          <strong>Emergency controls</strong>
          <span>
            {consoleMode === 'kill' && 'KILL SWITCH ENGAGED — fleet idle'}
            {consoleMode === 'throttle' && `THROTTLED — concurrency ≤ ${throttleLimit}%`}
            {consoleMode === 'safe' && 'SAFE MODE — autonomy disabled, approvals required'}
            {!consoleMode && 'All systems nominal'}
          </span>
        </div>
        {consoleMode && (
          <button className="ghost-button" onClick={disengageEmergency} type="button">
            Release
          </button>
        )}
      </div>

      <div className="emergency-controls__row">
        <button
          className={`emergency-button emergency-button--kill ${consoleMode === 'kill' ? 'is-engaged' : ''}`}
          onClick={() => setPendingAction(actions.kill)}
          type="button"
        >
          <FiIcons.FiPower /> Kill switch
        </button>

        <button
          className={`emergency-button emergency-button--throttle ${consoleMode === 'throttle' ? 'is-engaged' : ''}`}
          onClick={() => setPendingAction(actions.throttle)}
          type="button"
        >
          <FiIcons.FiSliders /> Throttle
          {consoleMode === 'throttle' && (
            <span className="emergency-button__steps">
              {THROTTLE_STEPS.map((step) => (
                <b
                  className={throttleLimit === step ? 'is-active' : ''}
                  key={step}
                  onClick={(domEvent) => {
                    domEvent.stopPropagation();
                    engageThrottle(step);
                  }}
                >
                  {step}%
                </b>
              ))}
            </span>
          )}
        </button>

        <button
          className={`emergency-button emergency-button--safe ${consoleMode === 'safe' ? 'is-engaged' : ''}`}
          onClick={() => setPendingAction(actions.safe)}
          type="button"
        >
          <FiIcons.FiShieldOff /> Safe mode
        </button>
      </div>

      {pendingAction && (
        <ConfirmActionModal
          action={pendingAction}
          onClose={() => setPendingAction(null)}
          onConfirm={(action) => {
            action.run();
            setPendingAction(null);
          }}
        />
      )}
    </div>
  );
}

export default EmergencyControls;
